/**
 * 🙈 USE BLIND DATE - Hook pour le mode Blind Date
 * Architecture Mozart/Jobs/Tesla
 */

import { useState, useCallback } from 'react';
import { api } from '../../services/api';
import { toast } from 'sonner';

export const useBlindDate = (customerId) => {
  // État
  const [isLoading, setIsLoading] = useState(false);
  const [blindProfiles, setBlindProfiles] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [revealReady, setRevealReady] = useState(false);

  // Constantes
  const MESSAGES_TO_REVEAL = 10;

  // ============================================
  // CHARGEMENT DES PROFILS BLIND
  // ============================================
  const loadBlindProfiles = useCallback(async () => {
    if (!customerId) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/blind-date/profiles/${customerId}`
      );
      const data = await response.json();
      
      if (data.success && data.profiles) {
        setBlindProfiles(data.profiles);
      }
    } catch (err) {
      console.error('useBlindDate loadBlindProfiles error:', err);
      toast.error('Erreur de chargement des profils');
    } finally {
      setIsLoading(false);
    }
  }, [customerId]);

  // ============================================
  // GESTION DES SESSIONS
  // ============================================
  const loadMySessions = useCallback(async () => {
    if (!customerId) return;
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/blind-date/my-sessions/${customerId}`
      );
      const data = await response.json();
      
      if (data.success && data.sessions) {
        setActiveSessions(data.sessions);
      }
    } catch (err) {
      console.error('useBlindDate loadMySessions error:', err);
    }
  }, [customerId]);

  const startBlindDate = useCallback(async (targetId) => {
    if (!customerId || !targetId) return null;
    
    setIsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/blind-date/start?customer_id=${customerId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ targetId })
        }
      );
      const data = await response.json();
      
      if (data.success && data.session) {
        setCurrentSession(data.session);
        toast.success('🙈 Blind Date lancé !');
        return data.session;
      } else {
        toast.error(data.message || 'Erreur lors du démarrage');
        return null;
      }
    } catch (err) {
      console.error('useBlindDate startBlindDate error:', err);
      toast.error('Erreur de connexion');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [customerId]);

  const openSession = useCallback(async (sessionId) => {
    if (!sessionId) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/blind-date/session/${sessionId}?customer_id=${customerId}`
      );
      const data = await response.json();
      
      if (data.success && data.session) {
        setCurrentSession(data.session);
        setMessages(data.messages || []);
        setRevealReady(data.session.canReveal || false);
      }
    } catch (err) {
      console.error('useBlindDate openSession error:', err);
      toast.error('Erreur de chargement de la session');
    } finally {
      setIsLoading(false);
    }
  }, [customerId]);

  // ============================================
  // MESSAGES
  // ============================================
  const sendMessage = useCallback(async (text) => {
    if (!currentSession || !text.trim()) return null;
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/blind-date/session/${currentSession.id}/message?customer_id=${customerId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: text.trim() })
        }
      );
      const data = await response.json();
      
      if (data.success && data.message) {
        setMessages(prev => [...prev, data.message]);
        
        // Vérifier si on peut reveal
        if (data.canReveal) {
          setRevealReady(true);
          toast.success('🎉 10 messages atteints ! Tu peux maintenant voir les photos !');
        }
        
        return data.message;
      }
    } catch (err) {
      console.error('useBlindDate sendMessage error:', err);
      toast.error('Erreur d\'envoi');
    }
    return null;
  }, [currentSession, customerId]);

  // ============================================
  // REVEAL
  // ============================================
  const revealPhotos = useCallback(async () => {
    if (!currentSession || !revealReady) return null;
    
    setIsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/blind-date/session/${currentSession.id}/reveal?customer_id=${customerId}`,
        { method: 'POST' }
      );
      const data = await response.json();
      
      if (data.success) {
        setCurrentSession(prev => ({
          ...prev,
          isRevealed: true,
          partnerPhoto: data.partnerPhoto
        }));
        toast.success('🌟 Tada ! Voici ton blind date !');
        return data;
      }
    } catch (err) {
      console.error('useBlindDate revealPhotos error:', err);
      toast.error('Erreur lors du reveal');
    } finally {
      setIsLoading(false);
    }
    return null;
  }, [currentSession, revealReady, customerId]);

  const closeSession = useCallback(() => {
    setCurrentSession(null);
    setMessages([]);
    setRevealReady(false);
  }, []);

  // ============================================
  // STATS
  // ============================================
  const getStats = useCallback(async () => {
    if (!customerId) return null;
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/blind-date/stats/${customerId}`
      );
      const data = await response.json();
      return data.success ? data.stats : null;
    } catch (err) {
      console.error('useBlindDate getStats error:', err);
      return null;
    }
  }, [customerId]);

  return {
    // État
    isLoading,
    blindProfiles,
    activeSessions,
    currentSession,
    messages,
    revealReady,
    MESSAGES_TO_REVEAL,
    
    // Actions
    loadBlindProfiles,
    loadMySessions,
    startBlindDate,
    openSession,
    sendMessage,
    revealPhotos,
    closeSession,
    getStats
  };
};

export default useBlindDate;
